import { useState, useMemo } from 'react';

interface PairConfig {
  name: string;
  pipValue: number;
}

const pairs: PairConfig[] = [
  { name: 'EUR/USD', pipValue: 10 },
  { name: 'GBP/USD', pipValue: 10 },
  { name: 'AUD/USD', pipValue: 10 },
  { name: 'NZD/USD', pipValue: 10 },
  { name: 'USD/JPY', pipValue: 9.2 },
  { name: 'USD/CAD', pipValue: 7.5 },
  { name: 'USD/CHF', pipValue: 9.0 },
  { name: 'EUR/GBP', pipValue: 12.5 },
  { name: 'EUR/JPY', pipValue: 9.2 },
  { name: 'GBP/JPY', pipValue: 9.2 },
];

const lotSizes = [0.01, 0.05, 0.1, 0.5, 1, 2, 5, 10];
const spreadPresets = [0.3, 0.6, 1.0, 1.5, 2.0, 3.0];

export default function InteractiveCostCalculator() {
  const [pair, setPair] = useState<PairConfig>(pairs[0]);
  const [lots, setLots] = useState(1);
  const [spread, setSpread] = useState(0.6);
  const [commissionPerSide, setCommissionPerSide] = useState(3.5);
  const [hasCommission, setHasCommission] = useState(true);
  const [holdingDays, setHoldingDays] = useState(1);
  const [swapPerDay, setSwapPerDay] = useState(0.3);
  const [slippagePips, setSlippagePips] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);

  const costs = useMemo(() => {
    const selectedPipValue = pair.pipValue;
    const spreadCost = spread * selectedPipValue * lots;
    const commissionCost = hasCommission ? commissionPerSide * 2 * lots : 0;
    const entryCost = spreadCost + commissionCost;
    const swapCost = swapPerDay * selectedPipValue * lots * holdingDays;
    const slippageCost = slippagePips * selectedPipValue * lots;
    const total = entryCost + swapCost + slippageCost;

    return { spreadCost, commissionCost, entryCost, swapCost, slippageCost, total };
  }, [pair, lots, spread, commissionPerSide, hasCommission, holdingDays, swapPerDay, slippagePips]);

  const breakevenPips = costs.total / (pair.pipValue * lots);

  return (
    <section className="relative w-full overflow-hidden" id="interactive-cost-calculator" style={{ backgroundColor: '#0A1628' }}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] opacity-[0.06]" style={{ backgroundColor: '#00A6B4' }}></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[100px] opacity-[0.05]" style={{ backgroundColor: '#2563EB' }}></div>

      <div className="relative z-10 w-full max-w-main mx-auto px-4 md:px-6 py-[64px] md:py-[88px]">
        {/* Header: label on left, title spanned out */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-10 mb-10 md:mb-14">
          <div className="flex-1">
            <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-white tracking-[-0.015em] max-w-[600px]">
              See your costs before you place the trade.
            </h2>
          </div>
          <p className="text-[14px] leading-[22px] text-white/45 max-w-[240px] flex-shrink-0 lg:text-right">
            Every parameter updates the calculation instantly.
          </p>
        </div>

        {/* Main tool panel */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* LEFT: Controls — glass panel */}
          <div
            className="flex-1 lg:max-w-[52%] rounded-[24px] p-6 md:p-8 backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {/* Pair + Lots row */}
            <div className="flex flex-col sm:flex-row gap-4 mb-5">
              {/* Currency pair */}
              <div className="flex-1">
                <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 mb-2">Pair</label>
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="w-full flex items-center justify-between h-[48px] px-4 rounded-xl text-[15px] font-bold text-white cursor-pointer transition-all hover:bg-white/[0.06]"
                    style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <span>{pair.name}</span>
                    <i className={`ri-arrow-down-s-line text-white/50 transition-transform ${showDropdown ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-20 max-h-[240px] overflow-y-auto shadow-[0_16px_48px_rgba(0,0,0,0.4)]" style={{ backgroundColor: '#1A2942', border: '1px solid rgba(255,255,255,0.1)' }}>
                      {pairs.map((p) => (
                        <button
                          key={p.name}
                          onClick={() => { setPair(p); setShowDropdown(false); }}
                          className={`w-full text-left px-4 py-3 text-[14px] font-medium cursor-pointer transition-colors ${
                            pair.name === p.name
                              ? 'text-white bg-white/[0.08]'
                              : 'text-white/60 hover:bg-white/[0.04] hover:text-white/80'
                          }`}
                        >
                          {p.name}
                          <span className="ml-2 text-[12px] text-white/35">~${p.pipValue}/pip</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Lots */}
              <div className="flex-shrink-0">
                <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 mb-2">Size · {lots} lot{lots !== 1 ? 's' : ''}</label>
                <div className="flex gap-1.5">
                  {lotSizes.slice(0, 5).map((l) => (
                    <button
                      key={l}
                      onClick={() => setLots(l)}
                      className={`w-[46px] h-[36px] rounded-lg text-[13px] font-bold cursor-pointer transition-all whitespace-nowrap ${
                        lots === l
                          ? 'text-white'
                          : 'text-white/40 hover:text-white/70'
                      }`}
                      style={lots === l ? { backgroundColor: '#2563EB' } : { backgroundColor: 'rgba(255,255,255,0.06)' }}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Spread selector */}
            <div className="mb-5">
              <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 mb-2">Spread · {spread.toFixed(1)} pips</label>
              <div className="flex flex-wrap gap-1.5">
                {spreadPresets.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSpread(s)}
                    className={`h-[36px] px-3.5 rounded-lg text-[13px] font-bold cursor-pointer transition-all whitespace-nowrap ${
                      spread === s ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                    style={spread === s ? { backgroundColor: '#2563EB' } : { backgroundColor: 'rgba(255,255,255,0.06)' }}
                  >
                    {s.toFixed(1)}p
                  </button>
                ))}
              </div>
            </div>

            {/* Commission */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-white/40">Commission</label>
                <button
                  onClick={() => setHasCommission(!hasCommission)}
                  type="button"
                  aria-label="Toggle commission"
                  className={`relative w-[48px] h-[26px] rounded-full transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A6B4]/40 ${
                    hasCommission
                      ? 'bg-[#00A6B4]'
                      : 'bg-white/[0.15] hover:bg-white/[0.2]'
                  }`}
                  style={hasCommission ? { boxShadow: '0 0 14px rgba(0,166,180,0.35)' } : {}}
                >
                  <span className={`absolute left-0 top-[2px] w-[22px] h-[22px] rounded-full bg-white transition-all duration-300 ${
                    hasCommission ? 'translate-x-[24px]' : 'translate-x-[2px]'
                  }`} />
                </button>
              </div>
              {hasCommission && (
                <div className="flex items-center gap-2" style={{ backgroundColor: 'rgba(0,166,180,0.08)', border: '1px solid rgba(0,166,180,0.15)', borderRadius: '12px', padding: '10px 14px' }}>
                  <span className="text-[13px] text-white/50">$</span>
                  <input
                    type="number"
                    value={commissionPerSide}
                    onChange={(e) => setCommissionPerSide(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-[56px] h-[34px] rounded-lg bg-white/[0.08] border border-white/[0.08] text-center text-[14px] font-bold text-white outline-none"
                    min="0"
                    step="0.1"
                  />
                  <span className="text-[12px] text-white/40">per side per lot</span>
                </div>
              )}
            </div>

            {/* Holding + Swap row */}
            <div className="flex flex-col sm:flex-row gap-4 mb-5">
              <div className="flex-1">
                <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 mb-2">Hold · {holdingDays}d</label>
                <input
                  type="range"
                  min="1"
                  max="30"
                  value={holdingDays}
                  onChange={(e) => setHoldingDays(parseInt(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#F59E0B', background: 'rgba(255,255,255,0.08)' }}
                />
              </div>
              {holdingDays > 1 && (
                <div className="flex-shrink-0">
                  <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 mb-2">Swap Rate</label>
                  <div className="flex items-center gap-2 px-3 h-[36px] rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <span className="text-[12px] text-white/40">-</span>
                    <input
                      type="number"
                      value={swapPerDay}
                      onChange={(e) => setSwapPerDay(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-[48px] h-[28px] rounded-md bg-white/[0.08] border border-white/[0.08] text-center text-[13px] font-bold text-white outline-none"
                      min="0"
                      step="0.1"
                    />
                    <span className="text-[11px] text-white/35 whitespace-nowrap">pips/d</span>
                  </div>
                </div>
              )}
            </div>

            {/* Slippage */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 mb-2">Slippage · {slippagePips.toFixed(1)} pips</label>
              <input
                type="range"
                min="0"
                max="5"
                step="0.5"
                value={slippagePips}
                onChange={(e) => setSlippagePips(parseFloat(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: '#7C3AED', background: 'rgba(255,255,255,0.08)' }}
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-white/25">Limit order</span>
                <span className="text-[10px] text-white/25">News event</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Results — solid panel */}
          <div className="flex-1 lg:max-w-[48%] flex flex-col gap-5">
            {/* Total cost highlight */}
            <div
              className="rounded-[24px] p-6 md:p-8 flex flex-col items-center text-center relative overflow-hidden"
              style={{ backgroundColor: '#00A6B4', boxShadow: '0 8px 40px rgba(0,166,180,0.25)' }}
            >
              <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-white/70 mb-3">Estimated Total Cost</span>
              <span className="text-[56px] md:text-[64px] font-[800] leading-none tracking-[-0.03em] text-white">${costs.total.toFixed(2)}</span>
              <span className="mt-3 text-[14px] font-semibold text-white/80">
                Market needs to move <strong className="text-white">{breakevenPips.toFixed(1)} pips</strong> to break even
              </span>
            </div>

            {/* Cost breakdown */}
            <div
              className="rounded-[20px] p-5 md:p-6 flex-1"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-white/35 mb-4 block">Cost Breakdown</span>

              <div className="space-y-2">
                {/* Spread */}
                <div className="flex items-center justify-between py-2.5 px-3 rounded-xl" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-2 h-2 rounded-full bg-[#2563EB]"></div>
                    <span className="text-[14px] text-white/80">Spread</span>
                  </div>
                  <span className="text-[15px] font-[700] text-white">${costs.spreadCost.toFixed(2)}</span>
                </div>

                {/* Commission */}
                <div className="flex items-center justify-between py-2.5 px-3 rounded-xl" style={{ backgroundColor: 'rgba(0,166,180,0.1)' }}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-2 h-2 rounded-full bg-[#00A6B4]"></div>
                    <span className="text-[14px] text-white/80">Commission</span>
                  </div>
                  <span className="text-[15px] font-[700] text-white">${costs.commissionCost.toFixed(2)}</span>
                </div>

                {/* Swap */}
                {holdingDays > 1 && (
                  <div className="flex items-center justify-between py-2.5 px-3 rounded-xl" style={{ backgroundColor: 'rgba(245,158,11,0.1)' }}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-2 h-2 rounded-full bg-[#F59E0B]"></div>
                      <span className="text-[14px] text-white/80">Swap</span>
                    </div>
                    <span className="text-[15px] font-[700] text-white">${costs.swapCost.toFixed(2)}</span>
                  </div>
                )}

                {/* Slippage */}
                {slippagePips > 0 && (
                  <div className="flex items-center justify-between py-2.5 px-3 rounded-xl" style={{ backgroundColor: 'rgba(124,58,237,0.1)' }}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-2 h-2 rounded-full bg-[#7C3AED]"></div>
                      <span className="text-[14px] text-white/80">Slippage</span>
                    </div>
                    <span className="text-[15px] font-[700] text-white">${costs.slippageCost.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Insight callout */}
              {hasCommission && costs.commissionCost > costs.spreadCost && (
                <div className="mt-4 p-3 rounded-xl flex items-start gap-2" style={{ backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}>
                  <i className="ri-error-warning-line text-[#FCD34D] text-sm mt-0.5 flex-shrink-0"></i>
                  <p className="text-[12px] leading-[17px] text-[#FCD34D]">
                    Commission (${costs.commissionCost.toFixed(2)}) exceeds spread (${costs.spreadCost.toFixed(2)}). The spread-only price tag is misleading.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-[12px] leading-[20px] text-white/25 max-w-[600px] mx-auto">
          Approximate pip values. Actual costs depend on broker pricing, execution, and market conditions.
        </p>
      </div>
    </section>
  );
}